import * as _ from 'underscore';
import {Injectable} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {LoggerService} from '../logger.service';
import {
    MAP,
    PollOptions,
    PollResultRecord,
    QuestionExt,
    QuestionsResponse,
    SubquestionResponseMap,
    TakerResultsResponse,
    TakerValidationResponse
} from '../../models';
import {CheckboxesGroupRequiredValidator} from '../../validators/checkboxesGroupRequired.validator';
import {CorrectAnswer} from '../../models/store.model';
import {CommonUtils} from '../utils/common.utils';
import {EventService} from '../event.service';
import {StoreEventsEnum} from '../../enums/StoreEventsEnum.enum';
import {StoreActionsEnum} from '../../enums/StoreActionsEnum.enum';
import {ActionEffect} from '../../classes/common.classes';
import {NoWhitespaceValidator} from '../../validators/noWhitespace.validator';
import {AuthorService} from './author.service';

declare var jQuery;

class QuestInfo {

    questNum: string;  // e.g. 'q5'
    sId: string;       // null or
    type: string;
    choicesCount: number;
    useOther: boolean;
    choiceOptions: any[];
    supportsCorrectAnswer: boolean;         // question supports correct answer (used for change listener)
    withSubquestionsHasCorrectAnswer: boolean;   // question with subquestions has correctAnswer(s) values
    correctAnswer: { correct: boolean, value: string, choiceNum?: number };

    constructor(questNum: string,  // e.g. 'q5'
                sId: string,       // null or
                type: string,
                choicesCount: number,
                useOther: boolean,
                choiceOptions: any[],
                supportsCorrectAnswer: boolean    // question supports correct answer (used for change listener)
    ) {
        this.questNum = questNum;
        this.sId = sId;
        this.type = type;
        this.choicesCount = choicesCount;
        this.useOther = useOther
        this.choiceOptions = choiceOptions;
        this.supportsCorrectAnswer = supportsCorrectAnswer;
    }

}

class OtherValueInfo {

    enable: boolean;
    value: any;
    control: FormControl;

    constructor(enable: boolean, value: any, control: FormControl) {
        this.enable = enable;
        this.value = value;
        this.control = control;
    }
}

export class FormMapperBean {
    constructor(public qForm: FormGroup,
                public isFirstQuestSetNum: boolean,
                public isLastQuestSetNum: boolean,
                public editMode: boolean,
                public noResponses: boolean) {
    }
}

@Injectable()
export class FormMapperService {

    constructor(protected fb: FormBuilder,
                private authorService: AuthorService,
                private eventService: EventService,
                protected logger: LoggerService) {
    }

    public makeQuestionForm(pollOptions: PollOptions,
                            questionsResponse: QuestionsResponse,
                            validationResponse: TakerValidationResponse | TakerResultsResponse,
                            editMode: boolean): FormMapperBean {

        const _self = this;
        const questionsGroup: MAP = {};
        const questInfoMap = new Map<string, QuestInfo>();    // this map is injected to qForm to provide additional data to templates
        const allDisabled = !editMode;

        const correctAnswers: MAP = pollOptions.hasCorrectAnswersFlag && validationResponse &&
        (validationResponse as TakerResultsResponse).correctAnswers ? (validationResponse as TakerResultsResponse).correctAnswers : null;
        const questionsTF = correctAnswers ? (validationResponse as TakerResultsResponse).takerResultsSummary.questions : null;

        const enteredValues = new MAP();
        const responses = editMode ? questionsResponse : validationResponse;
        if (responses) {
            // prepare previously entered values
            _.each<any>(responses['records'] || [], function (record: PollResultRecord) {
                enteredValues[record.questNum] = record;
            });

        }

        this.logger.log('init:',
            'questionsResponse', questionsResponse,
            'validationResponse', validationResponse,
            'enteredValues:', enteredValues,
            'correctAnswers:', correctAnswers);

        const mustAnswerAllFlag = pollOptions.mustAnswerAllFlag;
        const blockRequiredFlag = pollOptions.takerKind == 'author|edit';   // used to make correct answers not required
        let noResponses = true; // set to false if at least one response exists
		if (!questionsResponse.questions.length) {
            noResponses = false;
        }
        _.each<any>(questionsResponse['questions'], function (question: QuestionExt, qi: string) {

            const questNum: string = String(question.questNum);
            const qId = 'q' + questNum;

            const required = !blockRequiredFlag && (mustAnswerAllFlag || question.mustAnswerFlag);
            const commonValidator = required ? Validators.required : Validators.nullValidator;
            const commonTextValidator = required ? [Validators.required, NoWhitespaceValidator.validator()] : Validators.nullValidator;

            const record = enteredValues[questNum];
            const subqs: SubquestionResponseMap = record ? record['subquestions'] : {};
            _self.logger.log('V RECORD:', record, 'subqs:', subqs, 'questNum:', questNum);

            let qConfig: any;
            let currentValidator = null;

            switch (question.questTypeCode) {
                case 'N':   // "Numerical"
                case 'SC':  // "Scale"

                    currentValidator = commonValidator;

                    const questInfo = new QuestInfo(
                        qId,
                        '',
                        question.questTypeCode,
                        0,
                        false,
                        null,
                        true
                    );
                    if (correctAnswers && correctAnswers[questNum] && correctAnswers[questNum][0]) {
                        const tf = questionsTF[question.questNum - 1];
                        const isCorrect = tf && tf['subquestions'] ? tf['subquestions']['0'] == 'T' : false;
                        const value = correctAnswers[questNum][0].answerNumeric;
                        questInfo.correctAnswer = {
                            value: value,
                            correct: isCorrect
                        };
                    }
                    questInfoMap[qId] = questInfo;

                /* through */

                case 'LT':  // "Long Text Essay"
                case 'ST':  // "Short Text"
                {
                    const val = (subqs[0] ? subqs[0].value : (
                        question.questTypeCode === 'ST' &&  // only for 'ST' question type now
                        question.initialValue &&
                        pollOptions.vars &&
                        pollOptions.vars[question.initialValue] ? pollOptions.vars[question.initialValue] : ''
                    )) || '';
                    if (val) {
                        noResponses = false;
                    }
                    qConfig = [{
                        value: val,
                        disabled: allDisabled
                    }, currentValidator ? currentValidator : commonTextValidator];
                    _self.logger.log('V SIMPLE:', 'subqs:', subqs, 'questNum:', questNum, 'qConfig:', qConfig);
                    break;
                }

                case 'M1':  // "Pick One Radio Choice"
                case 'M3':  // "Pick One List Choice"
                {

                    const val = subqs[0] ? subqs[0].choiceNums[0] : '';    // Number

                    if (val) {
                        noResponses = false;
                    }

                    const questInfo = new QuestInfo(
                        qId,
                        '',
                        question.questTypeCode,
                        question.choicesCount,
                        val === question.choicesCount,
                        null,
                        true
                    );
                    if (correctAnswers && correctAnswers[questNum] && correctAnswers[questNum][0]) {
                        const tf = questionsTF[question.questNum - 1];
                        const isCorrect = tf && tf['subquestions'] ? tf['subquestions']['0'] == 'T' : false;
                        const choiceNum = correctAnswers[questNum][0].answerNumeric;
                        // choiceNum must be positive if selected, otherwise correct answer is not set
                        if (choiceNum >= 1) {
                            const choice = question.multiChoices[choiceNum - 1];
                            questInfo.correctAnswer = {
                                value: choice.choiceDesc,
                                correct: isCorrect,
                                choiceNum: (question.questTypeCode == 'M1' ? choiceNum : null) // only for M1
                            };
                        }
                    }
                    questInfoMap[qId] = questInfo;

                    if (question.questTypeCode === 'M3') {
                        // make choiceOptions for <mdb-select>
                        const choiceOptions = [];
                        (question.multiChoices || []).forEach(choice => choiceOptions.push({
                            value: choice.choiceNum,
                            label: choice.choiceDesc
                        }));
                        questInfoMap[qId].choiceOptions = choiceOptions;
                    }


                    if (question.otherResponseFlag) {
                        const valueMap = {
                            value: (subqs[0] ? subqs[0].shortText : ''),
                            disabled: allDisabled || (val !== question.choicesCount)
                        };

                        qConfig = [valueMap, commonTextValidator];
                        _self.logger.log('question.questTypeCode:', question.questTypeCode, 'questNum-Other:', questNum, 'qConfig:', qConfig);
                        questionsGroup[qId + 'Other'] = qConfig;
                    }

                    qConfig = [{
                        value: (question.questTypeCode == 'M1' ? val + '' : val)/* M1: must be String (control specific) */,
                        disabled: allDisabled
                    }, commonValidator];

                    _self.logger.log('V M1/M3:', 'subqs:', subqs, 'questNum:', questNum, 'qConfig:', qConfig);

                    break;
                }

                case 'M2':  // "Pick Multiple Choices"
                {
                    const choicesValueMap = new MAP();
                    _.each(subqs[0] ? subqs[0].choiceNums : [], function (value: number) {
                        choicesValueMap[value + ''/*must be String*/] = true;
                        noResponses = false;
                    });

                    const choicesGroup = new MAP();
                    if (question.choicesCount) {
                        for (let ci = 1; ci <= question.choicesCount; ci++) {
                            const cId = ci;
                            const value = choicesValueMap[ci] ? true : false;
                            choicesGroup[cId] = [{value: value, disabled: allDisabled}, commonValidator];
                            _self.logger.log('V M2:', 'subqs:', subqs,
                                'questNum:', questNum, 'choicesGroup[cId]:', choicesGroup[cId]);
                        }
                    }

                    questInfoMap[qId] = new QuestInfo(
                        qId,
                        '',
                        question.questTypeCode,
                        question.choicesCount,
                        question.otherResponseFlag && choicesValueMap[question.choicesCount + ''/*must be String*/] ? true : false,
                        null,
                        false
                    );

                    if (question.otherResponseFlag) {
                        const valueMap = {
                            value: (subqs[0] ? subqs[0].shortText : ''),
                            disabled: allDisabled || !choicesValueMap[question.choicesCount + ''/*must be String*/]
                        };

                        qConfig = [valueMap, commonTextValidator];
                        _self.logger.log('question.questTypeCode:', question.questTypeCode,
                            'questNum-Other:', questNum, 'qConfig:', qConfig);
                        questionsGroup[qId + 'Other'] = qConfig;
                    }

                    qConfig = _self.fb.group(choicesGroup,
                        {validator: (required ? CheckboxesGroupRequiredValidator.validator() : Validators.nullValidator)});
                    break;
                }

                case 'T1':  // "Pick One Choice Table"
                {
                    const subquestionsGroup = new MAP();
                    if (question.subquestCount) {
                        let hasCorrectAnswer = false;
                        for (let si = 1; si <= question.subquestCount; si++) {
                            const sId = si;
                            const subq = subqs[si];
                            const val = (subq ? (subq.choiceNums[0] || '') : '');    // Number
                            const strVal = val + ''/*must be String*/;

                            if (strVal) {
                                noResponses = false;
                            }

                            subquestionsGroup[sId] = [{value: strVal, disabled: allDisabled}, commonValidator];
                            _self.logger.log('V T1:',
                                'si:', si, 'subqs:', subqs,
                                'questNum:', questNum, 'subquestionsGroup[' + sId + ']:', subquestionsGroup[sId],
                                'T1val:', val, 'typeof T1val:', typeof val);

                            const qKey = qId + '_' + sId;

                            const subquestInfo = new QuestInfo(
                                qId,
                                sId + '',
                                question.questTypeCode,
                                question.choicesCount,
                                question.otherResponseFlag && val === question.choicesCount,
                                null,
                                true
                            );
                            if (correctAnswers && correctAnswers[questNum] && correctAnswers[questNum][String(sId)]) {
                                const tf = questionsTF[question.questNum - 1];
                                const isCorrect = tf && tf['subquestions'] ? tf['subquestions'][String(sId)] == 'T' : false;
                                const choiceNum = correctAnswers[questNum][sId].answerNumeric;
                                const choice = question.multiChoices[choiceNum - 1];
                                subquestInfo.correctAnswer = {
                                    value: choice.choiceDesc,
                                    correct: isCorrect,
                                    choiceNum: choiceNum
                                };
                                hasCorrectAnswer = true;
                            }
                            questInfoMap[qKey] = subquestInfo;

                            if (question.otherResponseFlag) {
                                const qOtherSId = qId + 'Other' + sId;
                                const valueMap = {
                                    value: (subq ? subq.shortText : ''),
                                    disabled: allDisabled || val !== question.choicesCount
                                };
                                qConfig = [valueMap, commonTextValidator];
                                _self.logger.log('question.questTypeCode:', question.questTypeCode,
                                    'questNum-Other:', questNum, 'qConfig:', qConfig);
                                questionsGroup[qOtherSId] = qConfig;
                            }
                        }
                        if (hasCorrectAnswer) {
                            // adding QuestInfo to question with subquestions just for withSubquestionsHasCorrectAnswer flag
                            const questInfo = new QuestInfo(
                                qId,
                                '',
                                question.questTypeCode,
                                question.choicesCount,
                                false,
                                null,
                                false
                            );
                            questInfo.withSubquestionsHasCorrectAnswer = true;
                            questInfoMap[qId] = questInfo;
                        }
                    }

                    qConfig = _self.fb.group(subquestionsGroup);
                    break;
                }

                case 'T2':  // "Pick Multiple Choices Table"
                {
                    const subquestionsGroup = new MAP();
                    if (question.subquestCount) {
                        for (let si = 1; si <= question.subquestCount; si++) {

                            const choicesValueMap = new MAP();
                            const subq = subqs[si + ''];
                            _.each(subq ? subq.choiceNums : [], function (value: number) {
                                choicesValueMap[value + ''/*must be String*/] = true;
                                noResponses = false;
                            });

                            const sId = si;

                            const choicesGroup = new MAP();
                            if (question.choicesCount) {
                                for (let ci = 1; ci <= question.choicesCount; ci++) {
                                    const cId = ci;
                                    const value = choicesValueMap[ci] ? true : false;
                                    choicesGroup[cId] = [{value: value, disabled: allDisabled}, commonValidator];
                                    _self.logger.log('V T2:', 'subqs:', subqs,
                                        'si:', si,
                                        'questNum:', questNum, 'subquestionsGroup[sId]:', subquestionsGroup[sId]);
                                }
                            }
                            subquestionsGroup[sId] = _self.fb.group(choicesGroup,
                                {validator: (required ? CheckboxesGroupRequiredValidator.validator() : Validators.nullValidator)});

                            const qKey = qId + '_' + sId;
                            questInfoMap[qKey] = new QuestInfo(
                                qId,
                                sId + '',
                                question.questTypeCode,
                                question.choicesCount,
                                question.otherResponseFlag && choicesValueMap[question.choicesCount + ''/*must be String*/] ? true : false,
                                null,
                                false
                            );

                            if (question.otherResponseFlag) {
                                const qOtherSId = qId + 'Other' + sId;
                                const valueMap = {
                                    value: (subq ? subq.shortText : ''),
                                    disabled: allDisabled || !choicesValueMap[question.choicesCount + ''/*must be String*/]
                                };
                                qConfig = [valueMap, commonTextValidator];
                                _self.logger.log('question.questTypeCode:', question.questTypeCode,
                                    'questNum-Other:', questNum, 'qConfig:', qConfig);
                                questionsGroup[qOtherSId] = qConfig;
                            }

                        }
                    }

                    qConfig = _self.fb.group(subquestionsGroup);
                    break;
                }

                default:
                    throw 'Not supported questTypeCode ' + question.questTypeCode;
            }

            questionsGroup[qId] = qConfig;
        });

        const qForm = this.fb.group(questionsGroup);

        // Extending Form with questInfo
        _.extend(qForm, {questInfo: questInfoMap});
        // this.logger.log('qForm.questInfo:', questInfo, 'questionsGroup:', questionsGroup);

        const isFirstQuestSetNum = questionsResponse.status ? questionsResponse.status.questSetNum === 1 : false;
        const isLastQuestSetNum = questionsResponse.status ? pollOptions.questSetNums === questionsResponse.status.questSetNum : false;

        if (editMode) {

            // support for inline correct answers editing in 'author|edit' mode
            if (pollOptions.hasCorrectAnswersFlag && pollOptions.takerKind == 'author|edit') {

                _.each((<any>qForm)['questInfo'],
                    function (info: QuestInfo, key: string) {
                        if (info.supportsCorrectAnswer) {
                            const control = qForm.get(info.questNum + (info.sId ? '.' + info.sId : ''));
                            //- _self.logger.log('supportsCorrectAnswer:', key, info.type, control, info);
                            if (control) {
                                // using debounceTime for two reasons:
                                // 1. send update back-end one time if user types fast
                                // 2. some controls (scale) produce two events with same value
                                control.valueChanges.debounceTime(1000).subscribe((value: any) => {
                                    if (control && control.valid) {
                                        const answers: CorrectAnswer[] = _self.makeAnswers(value, info, qForm);
                                        const questionClone = _self.cloneQuestion(questionsResponse, Number(info.questNum.substring(1)/*remove 'q'*/));
                                        questionClone.answers = answers;

                                        _self.authorService.checkResponsesNum(pollOptions.pollId, pollOptions.pollTitle, (recordsNum: number) => {
                                            _self.dispatchUpdateQuestion(questionClone, pollOptions.pollId);
                                        });

                                    }
                                });
                            }
                        }
                    });
            }

            // turn on/off other fields
            qForm.valueChanges.subscribe(
                (values: any) => {
                    _self.logger.log('qForm changed to: ', JSON.stringify(values), values);

                    _.each((<any>qForm)['questInfo'],
                        function (info: QuestInfo, key: string) {
                            if (info.choicesCount) {

                                const questNum = info['questNum'];
                                const sId = info['sId'];

                                const otherInfo = _self.extractOtherValue(info, values, qForm);

                                // const control = qForm.get(questNum + 'Other' + sId);
                                if (otherInfo.control) {
                                    info.useOther = otherInfo.enable;  // set whether other check is selected
                                    if (otherInfo.enable) {
                                        if (!otherInfo.control.enabled) {
                                            otherInfo.control.enable();
                                            ((questNum, sId) => {
                                                setTimeout(() => jQuery('input[name=' + questNum + 'Other' + sId + ']').focus().select(), 0);
                                            })(questNum, sId);
                                        }
                                    } else {
                                        if (!otherInfo.control.disabled) {
                                            otherInfo.control.disable();
                                        }
                                    }
                                }
                                _self.logger.log('onChange', 'questNum:', questNum, 'values:', values, 'otherInfo:', otherInfo);
                            }
                        });
                });
        }

        const bean = new FormMapperBean(qForm, isFirstQuestSetNum, isLastQuestSetNum, editMode, noResponses);

        this.logger.log('init questionsGroup:', questionsGroup, 'FormMapperBean:', bean);

        return bean;
    }

    public convertToServiceObj(values: any, questionsResponse: QuestionsResponse): any {

        const _self = this;
        let records: any = [];

        _.each<any>(questionsResponse.questions, function (question: any, qi: string) {

            const questNum: string = question.questNum;
            const qId = 'q' + questNum;
            let subquestionsVal: any = {};
            let controlValue: any = values[qId];
            if (controlValue === 'undefined') {
                controlValue = '';
            }

            _self.logger.log('QuestionsEditComponent: convertToServiceObj: controlValue:', controlValue,
                'questNum:', questNum, 'question:', question);

            switch (question.questTypeCode) {
                case 'LT':  // "Long Text Essay"
                case 'ST':  // "Short Text"
                case 'N':   // "Numerical"
                case 'SC':  // "Scale"
                    subquestionsVal['0'] = {'value': controlValue};
                    break;

                case 'M1':  // "Pick One Radio Choice"
                case 'M3':  // "Pick One List Choice"
                {
                    const choiceNums: any[] = [];
                    if (!CommonUtils.isNullOrUndefined(controlValue) && controlValue !== '') {
                        choiceNums.push(controlValue);
                    }
                    subquestionsVal['0'] = {'choiceNums': choiceNums};

                    if (question.otherResponseFlag) {
                        const otherValue = values[qId + 'Other'];
                        subquestionsVal['0']['shortText'] = otherValue;
                    }
                    break;
                }

                case 'M2':  // "Pick Multiple Choices"
                {
                    let choiceNums: string[] = [];

                    _.each<any>(controlValue, function (value: boolean, key: string) {
                        if (value === true) {
                            choiceNums.push(key);
                        }
                    });

                    subquestionsVal[0] = {'choiceNums': choiceNums};

                    if (question.otherResponseFlag) {
                        const otherValue = values[qId + 'Other'];
                        subquestionsVal['0']['shortText'] = otherValue;
                    }
                    break;
                }

                case 'T1':  // "Pick One Choice Table"
                    _.each<any>(controlValue, function (subqControlValue: any, subqKey: any) {

                        const choiceNums: string[] = [];

                        _.each<any>(subqControlValue, function (value: any, key: any) {
                            if (!CommonUtils.isNullOrUndefined(value) && value !== '') {
                                choiceNums.push(value);
                            }
                        });

                        subquestionsVal[subqKey] = {'choiceNums': choiceNums};

                        if (question.otherResponseFlag) {
                            const otherValue = values[qId + 'Other' + subqKey];
                            subquestionsVal[subqKey]['shortText'] = otherValue;
                        }

                    });
                    break;

                case 'T2':  // "Pick Multiple Choices Table"
                    _.each<any>(controlValue, function (subqControlValue: any, subqKey: any) {

                        const choiceNums: string[] = [];

                        _.each<any>(subqControlValue, function (value: boolean, key: string) {
                            if (value === true) {
                                choiceNums.push(key);
                            }
                        });

                        subquestionsVal[subqKey] = {'choiceNums': choiceNums};

                        if (question.otherResponseFlag) {
                            const otherValue = values[qId + 'Other' + subqKey];
                            subquestionsVal[subqKey]['shortText'] = otherValue;
                        }

                    });
                    break;

                default:
                    throw 'Not supported questTypeCode ' + question.questTypeCode;
            }

            // TODO: "shortText": "Short text maxlength(250)"

            records.push({
                'questNum': questNum,
                'subquestions': subquestionsVal
            });
        });

        const converted: any = {records: records};
        this.logger.log('QuestionsEditComponent: convertToServiceObj:', converted);
        return converted;
    }


    private extractOtherValue(info: QuestInfo, values: any, qForm: any): OtherValueInfo {

        const questNum = info.questNum;
        const sId = info.sId;
        const control = qForm.get(questNum + 'Other' + sId);

        let value;
        let enable;

        switch (info.type) {

            case 'M1':  // "Pick One Radio Choice"
            case 'M3':  // "Pick One List Choice"
                value = values[questNum];
                enable = value !== '' && (Number(value) === info['choicesCount']);
                break;

            case 'M2':  // "Pick Multiple Choices"
                // true/false array
                value = values[questNum][info['choicesCount']];
                enable = value;
                break;

            case 'T1':  // "Pick One Choice Table"
                value = values[questNum][sId];
                enable = value !== '' && (Number(value) === info['choicesCount']);
                break;

            case 'T2':  // "Pick Multiple Choices Table"
                // true/false array
                value = values[questNum][sId][info['choicesCount']];
                enable = value;
                break;

            // case 'LT':  // "Long Text Essay"
            // case 'ST':  // "Short Text"
            // case 'N':   // "Numerical"
            // case 'SC':  // "Scale"
            default:
                throw 'extractOtherValue: Not supported questInfo.type ' + info.type;
        }
        return new OtherValueInfo(enable, value, control);
    }

    private valueToCorrectAnswers(value: any, info: QuestInfo, qForm: FormGroup): CorrectAnswer[] {

        const answers: CorrectAnswer[] = [];

        switch (info.type) {

            case 'N':   // "Numerical"
            case 'SC':  // "Scale"
                answers.push({
                    questNum: Number(info.questNum.substring(1))/*remove 'q'*/,
                    subquestNum: 0,
                    answerNumeric: value
                });
                break;

            case 'M1':  // "Pick One Radio Choice"
            case 'M3':  // "Pick One List Choice"
                answers.push({
                    questNum: Number(info.questNum.substring(1))/*remove 'q'*/,
                    subquestNum: 0,
                    answerNumeric: value
                });
                break;

            case 'T1':  // "Pick One Choice Table"
                const values = qForm.value[info.questNum];
                Object.keys(values).forEach(key => {
                    if (values[key]) {
                        answers.push({
                            questNum: Number(info.questNum.substring(1))/*remove 'q'*/,
                            subquestNum: Number(key),
                            answerNumeric: Number(values[key])
                        });
                    }
                });
                break;

            // case 'LT':  // "Long Text Essay"
            // case 'ST':  // "Short Text"
            // case 'M2':  // "Pick Multiple Choices" - Note: not supported by DB schema
            // case 'T2':  // "Pick Multiple Choices Table" - Note: not supported by DB schema
            default:
                throw 'valueToCorrectAnswers: Not supported questInfo.type ' + info.type;
        }
        return answers;
    }

    private makeAnswers(value: any, info: QuestInfo, qForm: FormGroup): CorrectAnswer[] {
        const result = this.valueToCorrectAnswers(value, info, qForm);
        //- this.logger.log('supportsCorrectAnswer: changed to:', value, info, 'result:', result);
        return result;
    }

    private cloneQuestion(questionsResponse: QuestionsResponse, questNum: number): QuestionExt {
        const question = questionsResponse['questions'].find(question => question.questNum === questNum);
        // make deep copy of edited object
        const clone = question ? CommonUtils.jsonClone<QuestionExt>(question as QuestionExt) : null;
        return clone;
    }

    private dispatchUpdateQuestion(convertedQuestion: QuestionExt, pollId: number) {
        const action = {
            type: StoreActionsEnum.UPDATE_QUESTIONS,
            payload: {
                questions: [convertedQuestion], pollId: pollId,
                effect: new ActionEffect(`Updated correct answer for question "${convertedQuestion.text}"`),
                options: {questNum: convertedQuestion.questNum}
            }
        };
        this.eventService.emit({type: StoreEventsEnum.STORE_ACTION, payload: action});
    }

}
